/**
 * Login, as the prototype's «Kirish va Shell» section draws it: a form column on the
 * left and, from the tablet width up, a dark panel that shows what the owner sees after
 * signing in. Telegram and Google sit side by side as the secondary path. The one
 * orange action is «Kirish».
 */
import { useMutation } from "@tanstack/react-query";
import { Send } from "lucide-react";
import { useEffect, useState, type FormEvent, type JSX } from "react";
import { Link, useNavigate } from "react-router";

import type { TelegramWidgetUser } from "@/api/auth";
import { landingForUser } from "@/app/landing";
import { useTranslation } from "@/i18n";
import { ApiError } from "@/lib/api";
import { useAuthStore, type Role } from "@/store/auth";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";

import { AUTH_MESSAGES } from "./messages";
import { telegramSignInMessage, telegramSignInOutcome } from "./telegramOutcome";
import { signInWithTelegram } from "./telegramSession";
import { TelegramWidget } from "./TelegramWidget";
import { useAuthConfig } from "./useAuthConfig";

import "./login.css";

/** The demo accounts the seeder creates, so the roles can be compared side by side. */
const DEMO_ACCOUNTS: readonly { email: string; name: string; role: Role }[] = [
  { email: "nodira@ddtravel.uz", name: "Nodira Aliyeva", role: "operator" },
  { email: "jasur@ddtravel.uz", name: "Jasur Bekmurodov", role: "branch_manager" },
  { email: "madina@ddtravel.uz", name: "Madina Karimova", role: "moliyachi" },
  { email: "sardor@ddtravel.uz", name: "Sardor Alimov", role: "owner" },
];

const DEMO_PASSWORD = "demo12345";

/**
 * Whether to offer the one-click demo logins.
 *
 * Off unless the build explicitly asks for them. These accounts exist only where
 * `seed_demo` has run, and `seed_demo` refuses to run under production settings, so on
 * a real agency's login page every card would be a button that fails. Vite inlines this
 * at build time, so the production image does not contain the block or the addresses.
 */
const SHOW_DEMO_LOGINS = import.meta.env.VITE_SHOW_DEMO_LOGINS === "true";

/** Where the Telegram path stands. `waiting` is the POST in flight. */
type TelegramPhase = "idle" | "awaiting" | "waiting";

/** The preview panel's figures. They are illustrative and labelled as such on screen. */
const PREVIEW_FIGURES: readonly { key: string; label: string; value: string }[] = [
  { key: "revenue", label: "Tushum", value: "$97 740" },
  { key: "margin", label: "Marja", value: "18,4%" },
  { key: "ai", label: "AI tejadi", value: "123 soat" },
];

function LoginPreview(): JSX.Element {
  const { t } = useTranslation();
  return (
    <aside className="hidden flex-col justify-center bg-btn-ink px-10 text-btn-ink-ink md:flex lg:px-16">
      <div className="flex max-w-xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-btn-ink-ink/70">{t("auth.preview.kicker")}</span>
          <h2 className="text-h1 font-semibold text-btn-ink-ink">{t("auth.preview.title")}</h2>
        </div>

        <div className="flex flex-col gap-4 rounded-card border border-btn-ink-ink/15 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-base font-semibold">{t("auth.preview.agency")}</span>
            <span className="text-sm text-btn-ink-ink/70">{t("auth.preview.sample")}</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {PREVIEW_FIGURES.map((figure) => (
              <div key={figure.key} className="flex flex-col gap-1 rounded-control bg-btn-ink-ink/5 p-3">
                <span className="text-sm text-btn-ink-ink/70">{figure.label}</span>
                <span className="text-h1 font-semibold tabular-nums">{figure.value}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-btn-ink-ink/70">{t("auth.preview.note")}</p>
      </div>
    </aside>
  );
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const pollChallenge = useAuthStore((state) => state.pollChallenge);
  // D9.9: a correct password with two-factor on waits for the person's own Telegram.
  const [challenge, setChallenge] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [telegramPhase, setTelegramPhase] = useState<TelegramPhase>("idle");
  const [telegramMessage, setTelegramMessage] = useState<{ text: string; tone: "danger" | "neutral" } | null>(
    null,
  );

  const { telegramBot, googleClientId } = useAuthConfig();

  const telegramSignIn = useMutation({
    mutationFn: (payload: TelegramWidgetUser) => signInWithTelegram(payload),
    onSuccess: () => {
      // Read the role back from the store, as the email path does, so a moliyachi lands
      // on Moliya.
      void navigate(landingForUser(useAuthStore.getState().user), { replace: true });
    },
    onError: (caught: unknown) => {
      const status = caught instanceof ApiError ? caught.status : null;
      const outcome = telegramSignInOutcome(status);
      setTelegramPhase("idle");
      setTelegramMessage({
        // A rejected request is never a success, so "success" here can only be a bug;
        // it still gets a visible message rather than a silent reset.
        text: outcome === "success" ? AUTH_MESSAGES.genericError : telegramSignInMessage(outcome),
        tone: "danger",
      });
    },
  });

  async function attempt(withEmail: string, withPassword: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const outcome = await login(withEmail, withPassword);
      if (outcome.kind === "two-factor") {
        setChallenge(outcome.challenge);
        return;
      }
      // Read the role back from the store: `login` is what resolves it, and a
      // moliyachi lands on Moliya rather than on Bosh sahifa.
      void navigate(landingForUser(useAuthStore.getState().user), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.status === 403 ? t("auth.noMembership") : t("auth.invalidCredentials"));
      } else {
        setError(t("common.error"));
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (challenge === null) return undefined;
    let stopped = false;
    const timer = window.setInterval(() => {
      void pollChallenge(challenge)
        .then((outcome) => {
          if (stopped || outcome === "pending") return;
          stopped = true;
          window.clearInterval(timer);
          if (outcome === "signed-in") {
            void navigate(landingForUser(useAuthStore.getState().user), { replace: true });
            return;
          }
          setChallenge(null);
          setError(outcome === "denied" ? t("auth.twoFactor.denied") : t("auth.twoFactor.expired"));
        })
        .catch(() => {
          // A dropped poll is retried on the next tick; the challenge lives three minutes.
        });
    }, 2000);
    return (): void => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [challenge, navigate, pollChallenge, t]);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void attempt(email, password);
  }

  function openTelegram(): void {
    setTelegramMessage(null);
    setTelegramPhase("awaiting");
  }

  function cancelTelegram(): void {
    setTelegramPhase("idle");
    // The widget reports nothing when someone backs out, so the cancel is the only
    // signal there is; say what happened rather than leave the panel silently gone.
    setTelegramMessage({ text: AUTH_MESSAGES.telegramDeclined, tone: "neutral" });
  }

  function onTelegramAuth(payload: TelegramWidgetUser): void {
    setTelegramPhase("waiting");
    setTelegramMessage(null);
    telegramSignIn.mutate(payload);
  }

  return (
    <main className="grid min-h-dvh bg-canvas md:grid-cols-2">
      <section className="flex min-h-dvh flex-col px-4 py-6 md:px-10">
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded-small bg-btn-primary text-sm font-semibold text-btn-primary-ink">
            K
          </span>
          <span className="text-base font-semibold text-ink">{t("app.name")}</span>
        </div>

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <h1 className="text-h2 font-semibold text-ink">{t("auth.title")}</h1>

            {challenge === null ? null : (
              <div role="status" className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4">
                <span className="flex items-center gap-2 text-base font-medium text-ink">
                  <Send strokeWidth={1.5} aria-hidden="true" />
                  {t("auth.twoFactor.title")}
                </span>
                <p className="text-sm text-ink-muted">{t("auth.twoFactor.body")}</p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setChallenge(null);
                  }}
                >
                  {t("auth.twoFactor.cancel")}
                </Button>
              </div>
            )}

            <form onSubmit={onSubmit} className={challenge === null ? "flex flex-col gap-4" : "hidden"}>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t("auth.email")}</span>
                <Input
                  type="email"
                  autoComplete="email"
                  value={email}
                  invalid={error !== null}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-ink">{t("auth.password")}</span>
                  <Link to="/forgot-password" className="k-login__forgot text-sm">
                    {t("auth.forgot")}
                  </Link>
                </span>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  invalid={error !== null}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                  required
                />
                {error === null ? null : (
                  <span role="alert" className="text-sm text-danger-ink">
                    {error}
                  </span>
                )}
              </label>

              <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
                {busy ? t("auth.submitting") : t("auth.submit")}
              </Button>
            </form>

            {telegramBot === "" && googleClientId === "" ? null : (
              <div className="k-login__social">
                <div className="k-login__divider" aria-hidden="true">
                  <span>{AUTH_MESSAGES.orDivider}</span>
                </div>

                <div className="k-login__socialRow">
                  {telegramBot === "" ? null : (
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      loading={telegramPhase === "waiting"}
                      disabled={telegramPhase === "waiting" || busy}
                      aria-expanded={telegramPhase === "awaiting"}
                      onClick={openTelegram}
                    >
                      <Send strokeWidth={1.5} aria-hidden="true" />
                      {AUTH_MESSAGES.telegramButton}
                    </Button>
                  )}
                  {googleClientId === "" ? null : (
                    <Button variant="secondary" size="lg" className="w-full">
                      {AUTH_MESSAGES.googleButton}
                    </Button>
                  )}
                </div>

                {telegramPhase === "awaiting" && telegramBot !== "" ? (
                  <div className="k-login__telegram">
                    <TelegramWidget
                      botUsername={telegramBot}
                      callbackName="kapitanTelegramLogin"
                      onAuth={onTelegramAuth}
                    />
                    <Button variant="ghost" size="sm" onClick={cancelTelegram}>
                      {t("common.cancel")}
                    </Button>
                  </div>
                ) : null}

                {telegramMessage === null ? null : (
                  <p
                    role={telegramMessage.tone === "danger" ? "alert" : "status"}
                    className={
                      telegramMessage.tone === "danger"
                        ? "k-login__telegramMessage text-sm text-danger-ink"
                        : "k-login__telegramMessage text-sm text-ink-muted"
                    }
                  >
                    {telegramMessage.text}
                  </p>
                )}
              </div>
            )}

            {!SHOW_DEMO_LOGINS ? null : (
              <div className="k-login__demo">
                <span className="k-login__demoTitle">Demo sifatida kirish</span>
                <div className="k-login__demoGrid">
                  {DEMO_ACCOUNTS.map((account) => (
                    <button
                      key={account.email}
                      type="button"
                      className="k-login__demoCard"
                      disabled={busy}
                      onClick={() => {
                        void attempt(account.email, DEMO_PASSWORD);
                      }}
                    >
                      <strong className="k-login__demoName">{account.name}</strong>
                      <span className="k-login__demoRole">{t(`role.${account.role}`)}</span>
                      <span className="k-login__demoHint">{t(`role.${account.role}.hint`)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-sm text-ink-subtle">© 2026 Kapitan</p>
      </section>

      <LoginPreview />
    </main>
  );
}
